/**
 * `OpenModes` (1.I-2 deliverable 3), the root `Wrapper`: the shell's `OpenDocContext` handler
 * with the four open modes, the peeks, and `NavContext` behind `useOpenIn`. With `shell` off it
 * renders its children only (same tree, nothing attached): the handler is the one above, no peek
 * is drawn and `useOpenIn` is null.
 *
 * Stage s3 (the peek's speed): an open that shows a document in a peek or pane 2 starts its
 * `docs.get` (1.X's `prefetchDoc`, which the pane's session takes) and the pane chunk's import
 * before the state update, and keeps the answer for the preview (`PeekAnswerContext`); a `full`
 * open keeps the route's own prefetch. A primary `pointerdown` over a mention chip warms the
 * chunk (`warm.ts`). Neither exists while `shell` is off.
 */
import type { DocumentV1 } from '@codex/contracts';
import { isApplePlatform } from '@codex/ui';
import { useLocation, useNavigate } from '@tanstack/react-router';
import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PREFETCH_MAX_AGE_MS, prefetchDoc } from '../compose/prefetch.js';
import { isFramelessPath, safeFlags } from '../shell/frame-rule.js';
import { OpenDocContext, type OpenDocHandler } from '../shell/open-doc.js';
import { useShellSession } from '../shell/session.js';
import { CenterPeek } from './center-peek.js';
import { decideOpen, sourceOf } from './decide-open.js';
import {
  type NavApi,
  NavApiContext,
  NavContext,
  type NavOpen,
  PeekAnswerContext,
  type PeekAnswers,
} from './nav-context.js';
import {
  closePane,
  INITIAL_NAV,
  type NavState,
  onLocationChange,
  type PaneName,
  planOpen,
} from './nav-state.js';
import { paneControls } from './pane-controls.js';
import { loadPaneModule } from './pane-module.js';
import { clearPeekTrace, markPeek } from './peek-trace.js';
import { SidePeek } from './side-peek.js';
import { shouldWarm } from './warm.js';
import './nav.css';

/** How many prefetched answers are kept for previews. */
const HELD_ANSWERS = 4;

export function OpenModes({ children }: { children?: ReactNode }) {
  const me = useShellSession();
  // A malformed `auth.me` answer fails closed: open modes off, children still rendered.
  const enabled = safeFlags(me)?.shell === true;
  const outer = useContext(OpenDocContext);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const ref = useRef<NavState>(INITIAL_NAV);
  const [state, setState] = useState<NavState>(INITIAL_NAV);

  const update = useCallback((change: (state: NavState) => NavState) => {
    const next = change(ref.current);
    if (next === ref.current) return;
    ref.current = next;
    setState(next);
  }, []);

  // The answers `open()` prefetched, by document, for the panes' previews (the newest few).
  const held = useRef(new Map<string, { answer: Promise<DocumentV1>; at: number }>());
  const startPane = useCallback((docId: string) => {
    clearPeekTrace();
    markPeek('open');
    const answer = prefetchDoc(docId);
    markPeek('fetch');
    answer.then(
      () => markPeek('answer'),
      () => undefined,
    );
    held.current.delete(docId);
    held.current.set(docId, { answer, at: Date.now() });
    for (const key of held.current.keys()) {
      if (held.current.size <= HELD_ANSWERS) break;
      held.current.delete(key);
    }
    loadPaneModule().then(
      () => markPeek('chunk'),
      () => undefined,
    );
  }, []);
  const answers = useCallback<PeekAnswers>((docId) => {
    const entry = held.current.get(docId);
    return entry && Date.now() - entry.at <= PREFETCH_MAX_AGE_MS ? entry.answer : null;
  }, []);

  const open = useCallback<NavOpen>(
    (request) => {
      const plan = planOpen(ref.current, request);
      // A peek or pane 2 fetches before React renders it; a `full` open has the route's prefetch.
      // Only when a pane shows something new: a second click on the chip already shown changes
      // no stack, mounts no pane, and so would leave a request nobody takes.
      const changes =
        plan.state.side !== ref.current.side ||
        plan.state.center !== ref.current.center ||
        plan.state.split !== ref.current.split;
      if (plan.navigate === undefined && changes) startPane(request.target.docId);
      update(() => plan.state);
      if (plan.navigate !== undefined) void navigateRef.current({ href: plan.navigate });
    },
    [update, startPane],
  );

  // The warm-up: a primary press on a mention chip starts the pane chunk's import.
  useEffect(() => {
    if (!enabled) return;
    const warm = (event: PointerEvent) => {
      if (shouldWarm(event)) loadPaneModule().catch(() => undefined);
    };
    document.addEventListener('pointerdown', warm, { capture: true, passive: true });
    return () => document.removeEventListener('pointerdown', warm, { capture: true });
  }, [enabled]);

  const handler = useCallback<OpenDocHandler>(
    (docId, event) => {
      if (!event) {
        open({ target: { docId }, mode: 'full', source: 'main' });
        return;
      }
      const current = 'currentTarget' in event ? event.currentTarget : null;
      const element = current instanceof Element ? current : null;
      const decision = decideOpen(event, {
        apple: isApplePlatform(),
        // With no element to read, a plain activation opens the page.
        inNav: element === null || element.closest('nav') !== null,
      });
      if (decision === 'browser') return;
      event.preventDefault();
      open({ target: { docId }, mode: decision, source: sourceOf(element) });
    },
    [open],
  );

  const pathname = useLocation({ select: (location) => location.pathname });
  const seen = useRef(pathname);
  useEffect(() => {
    if (seen.current === pathname) return;
    seen.current = pathname;
    update(onLocationChange);
    // A frameless page has no content region to hold pane 2.
    if (isFramelessPath(pathname)) update((s) => closePane(s, 'split'));
  }, [pathname, update]);

  const api = useMemo<NavApi>(() => ({ state, open, update }), [state, open, update]);
  const controls = (which: PaneName) => paneControls(api, which);

  const providedOpen = useMemo(() => (enabled ? open : null), [enabled, open]);
  return (
    <OpenDocContext.Provider value={enabled ? handler : outer}>
      <NavContext.Provider value={providedOpen}>
        <NavApiContext.Provider value={enabled ? api : null}>
          <PeekAnswerContext.Provider value={answers}>
            {children}
            {enabled && state.side && <SidePeek stack={state.side} {...controls('side')} />}
            {enabled && state.center && <CenterPeek stack={state.center} {...controls('center')} />}
          </PeekAnswerContext.Provider>
        </NavApiContext.Provider>
      </NavContext.Provider>
    </OpenDocContext.Provider>
  );
}
